// src/components/SearchBar.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  TextField,
  IconButton,
  InputAdornment,
  Divider,
  SxProps,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { colors } from '../../utils/constants';

interface SearchInputProps {
  section: string;
  searchText: string;
  handleSearch: (text: string) => void;
}

const SearchInput: React.FC<SearchInputProps> = React.memo(
  ({ section, searchText, handleSearch }: SearchInputProps) => {
    const [localSearchText, setLocalSearchText] = useState<string>(searchText);
    const inputRef = useRef<HTMLInputElement>(null);
    const isFocusedRef = useRef<boolean>(false);

    // Debounce function to delay API calls
    const debounce = (func: (...args: any[]) => void, delay: number) => {
      let timeoutId: NodeJS.Timeout;
      return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
      };
    };

    // Debounced search handler
    const debouncedSearch = useCallback(
      debounce((value: string) => {
        handleSearch(value);
      }, 500),
      [handleSearch]
    );

    // Handle search input change
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLocalSearchText(value);
      debouncedSearch(value);
    };

    // Clear search
    const handleClearSearch = () => {
      setLocalSearchText('');
      handleSearch('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    // Track focus state
    const handleFocus = () => {
      isFocusedRef.current = true;
    };

    const handleBlur = () => {
      isFocusedRef.current = false;
    };

    // Sync localSearchText with searchText prop
    useEffect(() => {
      setLocalSearchText(searchText);
    }, [searchText]);

    // Restore focus after re-render
    useEffect(() => {
      if (isFocusedRef.current && inputRef.current) {
        inputRef.current.focus();
      }
    }, [searchText]);

    return (
      <TextField
        inputRef={inputRef}
        variant='outlined'
        size='small'
        placeholder={`Search ${section}...`}
        value={localSearchText}
        onChange={handleSearchChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        slotProps={{
          input: {
            sx: SearchInputStyles,
            endAdornment: (
              <>
                {localSearchText && (
                  <InputAdornment position='end'>
                    <IconButton onClick={handleClearSearch} size='small'>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )}
                <Divider orientation='vertical' flexItem />
                <InputAdornment
                  position='end'
                  sx={{
                    '&.MuiInputAdornment-root': {
                      marginLeft: '0px',
                    },
                  }}
                >
                  <SearchIcon sx={{ color: 'gray', padding: '6px' }} />
                </InputAdornment>
              </>
            ),
          },
        }}
        sx={{ width: '60%', backgroundColor: colors.white }}
      />
    );
  }
);

// Set displayName for ESLint react/display-name
SearchInput.displayName = 'SearchInput';
export default SearchInput;

const SearchInputStyles: SxProps = {
  padding: '6px',
  borderRadius: '8px',
  height: '48px',
  fontSize: '14px',
};
